import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close,
  KeyboardArrowRight,
  PauseRounded,
  PlayArrowRounded,
} from '@mui/icons-material';
import CustomSwitch from '@src/components/Switch';
import { COLOR } from '@src/styles/color';
import {
  LIMIT_CLONED_VOICE,
  VOICE_COMPLETED,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import route from '@src/constants/route';

import {
  StyledEditVoiceInformationMobile,
  StyledNewAudioSlider,
  StyledNewDialogVoiceDetailDesktop,
  StyledNewDialogVoiceDetailMobile,
  StyledNewVoiceDetailInfo,
} from './index.style';
import EditVoiceInformation from './EditVoiceInformation';

const Avatar = ({ avatar, name, handleCloseDialog }) => (
  <Box className="avatar-wrapper">
    <img src={avatar} alt="avatar" className="avatar" />
    <Typography className="name">{name}</Typography>
    <Box className="close-btn" onClick={handleCloseDialog}>
      <Close className="close-icon" />
    </Box>
  </Box>
);

const NewVoiceDetailInfo = ({
  open,
  onClose,
  handleChangeVoiceData,
  setWarningTitle,
  setWarningContent,
  setWarningTextConfirm,
  setOpenWarningDialog,
  setWarningTextConfirmValue,
  setIsShowWarningContent,
  onShowPopupLimitNumberVoiceCloning,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  // Redux
  const { voice } = useSelector((state) => state.voiceVoiceCloning);

  // Ref
  const audioRef = useRef(null);

  // State
  const [voiceInfo, setVoiceInfo] = useState(voice);
  const [audioLink, setAudioLink] = useState('');
  const [playAudio, setPlayAudio] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);
  const [activeVoice, setActiveVoice] = useState(
    voice?.status === VOICE_STATUS.USING,
  );
  const [errorActiveVoice, setErrorActiveVoice] = useState(false);
  const [loading] = useState(false);
  const [disabledInteract, setDisabledInteract] = useState(false);
  const [isDisabledDelete, setIsDisabledDelete] = useState(false);
  const [isEditVoiceInfo, setIsEditVoiceInfo] = useState(false);

  const handleUseVoiceNow = () => {
    dispatch(actions.synthesisRequest.updateVoice(voiceInfo));
    history.push(route.TTS);
  };

  const handleActivationError = (errorCode) => {
    if (errorCode && errorCode === LIMIT_CLONED_VOICE) {
      onShowPopupLimitNumberVoiceCloning(true);
      setActiveVoice(false);
      return;
    }
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: 'activeVoiceFailed',
      }),
    );
    setActiveVoice(false);
  };

  const showWarningDeactiveVoiceDialog = () => {
    onClose();
    setWarningTitle(t('stopVoiceActivation'));
    setWarningContent(t('youCannotUseVoiceUntilReActive'));
    setWarningTextConfirm('confirmUnactiveVoice');
    setWarningTextConfirmValue(voice);
    setIsShowWarningContent(true);
    setOpenWarningDialog(true);
  };

  const processActiveVoice = async () => {
    setErrorActiveVoice(false);
    setActiveVoice(true);

    const res = await apis.voiceCloning.updateVoiceCloningVoice(voice?.id, {
      status: VOICE_STATUS.USING,
    });

    if (!res.status) {
      handleActivationError(res?.errorCode);
      return;
    }

    handleChangeVoiceData(voice.id, {
      status: VOICE_STATUS.USING,
      subStatus: res.result.voice?.subStatus,
    });

    setVoiceInfo({
      ...voice,
      status: VOICE_STATUS.USING,
      subStatus: res.result.voice?.subStatus,
    });

    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'activeVoiceSuccess',
      }),
    );
  };

  //   Handle change toggle
  const handleToggleActiveVoice = async () => {
    if (!activeVoice) await processActiveVoice();
    else showWarningDeactiveVoiceDialog();
  };

  const handleDeleteVoice = async () => {
    onClose();
    setWarningTitle(t('confirmDeleteVoice'));
    setWarningTextConfirm('confirmDeleteVoiceText');
    setWarningTextConfirmValue(voice);
    setIsShowWarningContent(false);
    setOpenWarningDialog(true);
  };

  const handleTogglePlayAudio = () => {
    audioRef.current.load();
    if (playAudio) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlayAudio(!playAudio);
  };

  // Handle slider change to update the current time of the audio
  const handleSliderChange = (event, newValue) => {
    const audio = audioRef.current;
    const newTime = (newValue / 100) * audio.duration;
    audio.currentTime = newTime;
    setSliderValue(newValue);
  };

  const onAudioEnded = () => {
    setPlayAudio(false);
    setSliderValue(0);
  };

  const handleCloseDialog = () => {
    onClose();
    setPlayAudio(false);
    setSliderValue(0);
    setErrorActiveVoice(false);
    setActiveVoice(false);
    setIsEditVoiceInfo(false);
  };

  const handleAudioTimeUpdate = () => {
    const time = audioRef.current.currentTime;
    setSliderValue((time / audioRef.current.duration) * 100);
  };

  const updateVoice = (newVoice) => {
    handleChangeVoiceData(newVoice.id, newVoice);
    setVoiceInfo(newVoice);
  };

  const goBack = () => setIsEditVoiceInfo(false);

  useEffect(() => {
    setVoiceInfo(voice);
    setActiveVoice(voice?.status === VOICE_STATUS.USING);
    setErrorActiveVoice(false);
    setDisabledInteract(!VOICE_COMPLETED.includes(voice?.status));
    setIsDisabledDelete(
      ![
        VOICE_STATUS.RECORDING,
        VOICE_STATUS.REJECTED,
        VOICE_STATUS.READY,
        VOICE_STATUS.USING,
        VOICE_STATUS.FAILED,
        VOICE_STATUS.SUSPENDED,
      ].includes(voice?.status),
    );
    if (voice?.sampleVoices?.[0]?.sampleLink)
      setAudioLink(voice?.sampleVoices?.[0]?.sampleLink);
  }, [voice]);

  // Update the slider value as the audio plays
  useEffect(() => {
    const audio = audioRef.current;

    if (audio) audio.addEventListener('ended', onAudioEnded);

    return () => {
      if (audio) audio.removeEventListener('ended', onAudioEnded);
    };
  }, []);

  const AudioSample = () => (
    <Box className="audio-sample-wrapper">
      <IconButton
        className={`button-play ${!audioLink ? 'disabled' : ''}`}
        onClick={handleTogglePlayAudio}
        disabled={!audioLink}
      >
        {playAudio ? <PauseRounded /> : <PlayArrowRounded />}
      </IconButton>
      <StyledNewAudioSlider
        value={sliderValue}
        step={1}
        min={0}
        max={100}
        onChange={handleSliderChange}
      />
    </Box>
  );

  const ActiveVoice = () => (
    <Box className="active-audio-wrapper">
      <Box className="title-wrapper">
        <Typography className="title">{t('activeNow')}</Typography>
        <CustomSwitch
          checked={activeVoice}
          noneCheckedcolor={COLOR.secondary[80]}
          checkedColor={COLOR.greenV2.default}
          onChange={handleToggleActiveVoice}
          disabled={disabledInteract}
        />
      </Box>
      <Typography className="note">{t('activeVoiceNote')}</Typography>
      {errorActiveVoice && (
        <Typography className="error">{t('activeVoiceMaximum')}</Typography>
      )}
      {voiceInfo?.status === VOICE_STATUS.USING && (
        <Button
          variant="contained"
          className="use-now-btn"
          onClick={handleUseVoiceNow}
        >
          {t('useNow')}
        </Button>
      )}
    </Box>
  );

  if (isMobile)
    return (
      <>
        <StyledNewDialogVoiceDetailMobile
          open={open}
          onClose={handleCloseDialog}
        >
          <>
            <Backdrop sx={{ zIndex: '9999999' }} open={loading}>
              <CircularProgress />
            </Backdrop>
            <StyledNewVoiceDetailInfo
              disabledInteract={disabledInteract}
              isDisabledDelete={isDisabledDelete}
            >
              <Avatar
                avatar={voiceInfo?.avatar}
                name={voiceInfo?.name}
                handleCloseDialog={handleCloseDialog}
              />
              {!disabledInteract && <AudioSample />}
              <ActiveVoice />
              <Box
                className="voice-info-wrapper"
                onClick={() => setIsEditVoiceInfo(true)}
              >
                <Typography className="title">
                  {t('voiceInformation')}
                </Typography>
                <KeyboardArrowRight className="icon" />
              </Box>
              <Button
                variant="outlined"
                className="delete-btn"
                disabled={isDisabledDelete}
                onClick={handleDeleteVoice}
              >
                {t('deleteVoice')}
              </Button>
            </StyledNewVoiceDetailInfo>
          </>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            src={audioLink}
            ref={audioRef}
            style={{ display: 'none' }}
            onEnded={onAudioEnded}
            onTimeUpdate={handleAudioTimeUpdate}
          >
            <source src={audioLink} type="audio/ogg" />
          </audio>
        </StyledNewDialogVoiceDetailMobile>

        {isEditVoiceInfo && (
          <StyledEditVoiceInformationMobile open onClose={handleCloseDialog}>
            <EditVoiceInformation updateVoice={updateVoice} goBack={goBack} />
          </StyledEditVoiceInformationMobile>
        )}
      </>
    );

  return (
    <StyledNewDialogVoiceDetailDesktop open={open} onClose={handleCloseDialog}>
      {!isEditVoiceInfo ? (
        <StyledNewVoiceDetailInfo
          disabledInteract={disabledInteract}
          isDisabledDelete={isDisabledDelete}
        >
          <Avatar
            avatar={voiceInfo?.avatar}
            name={voiceInfo?.name}
            handleCloseDialog={handleCloseDialog}
          />
          {!disabledInteract && <AudioSample />}
          <ActiveVoice />
          <Box
            className="voice-info-wrapper"
            onClick={() => setIsEditVoiceInfo(true)}
          >
            <Typography className="title">{t('voiceInformation')}</Typography>
            <KeyboardArrowRight className="icon" />
          </Box>
          <Button
            variant="outlined"
            className="delete-btn"
            disabled={isDisabledDelete}
            onClick={handleDeleteVoice}
          >
            {t('deleteVoice')}
          </Button>
        </StyledNewVoiceDetailInfo>
      ) : (
        <EditVoiceInformation updateVoice={updateVoice} goBack={goBack} />
      )}

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        src={audioLink}
        ref={audioRef}
        style={{ display: 'none' }}
        onTimeUpdate={handleAudioTimeUpdate}
        onEnded={onAudioEnded}
      >
        <source src={audioLink} type="audio/ogg" />
      </audio>
    </StyledNewDialogVoiceDetailDesktop>
  );
};

export default NewVoiceDetailInfo;
